// GroupsPage.tsx · v12.10.26 (#2138 the Call path tick is gone - Call path / Call route belong to the VS Support level) · 2026-10-30 · VoiceSpectrum AI
// GroupsPage.tsx · v12.10.14 (#1618: Call path is a Full-tier tick shown in the See row, not red) · 2026-10-12 · VoiceSpectrum AI
// GroupsPage.tsx · v12.10.10 (#1399 Assign tick under Handle; #1400 'Export' row with Download recording, no empty Full group for messages) · 2026-10-08 · VoiceSpectrum AI
// GroupsPage.tsx · v12.10.9.4 (#1372 FUTURE rows disabled, #1377 area lines, #1378 five fixed columns; #1381 one tick per action) · 2026-10-07 · VoiceSpectrum AI
// VoiceSpectrum.Web.T2 · v12.10.9 (Security Phase 1 – #1258/#1259/#1260/#1261) · 2026-10-06
// GroupsPage.tsx - Settings › Security › User groups (#1260; the prototype's Roles page, scRolesPage, renamed - S17).
//   Pills A-Z (§28): one selected = edit it, several = compare (editable; "Show only differences" on by default).
//   Each area row: None · View · Work · Full · Custom (§32); Custom opens the row's own checklist grouped View / Work / Full
//   (§33) and the level is derived from the ticks. "All rows" and per-Settings-area rows set many at once (§18 tree rows).
//   Rename / Duplicate / Reset to standard (built-in) / Delete (custom); "+ Add group" from a group or a template
//   (Auditor, Read-only); the "People in this group" drawer. Edits are a draft saved with "Save groups" (Confirm it's you).
//   No escalation (§36): nothing can be ticked beyond the viewer's own access; groups above it are read-only here.
//   Owner always has everything (locked), and can be renamed.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { HelpTooltip, type HelpSection } from '../../components/aiagent/HelpTooltip';
import { isConfirmCancelled, withConfirm } from '../../components/security/ConfirmItsYou';
import { Avatar, STD_NAMES, displayName, groupManagesUsers, identity, isInvite, sortGroups } from '../../components/security/people';
import { openYourAccount } from '../../components/security/YourAccountDrawer';
import { Ic, InlineError, ScModal, Toggle, plural, useConfirm, useEscClose } from '../../components/security/ui';
import { securityApi, securityErrorMessage, type Area, type Grants, type Group, type GroupTemplate, type Level, type UserRow } from '../../lib/securityApi';
import { SECURITY_CHANGED_EVENT, useAccess } from '../../lib/useAccess';
import { toast } from '../../lib/toast';
import { LV, LV_ICON, addsBeyond, customHint, grantsEqual, levelHelp, levelOf, lvList, preset, rowEqual } from './groupLevels';
import { GROUPS_KEY, USERS_KEY, type SecPageState } from './PlatformPage';
import UserDrawer from './UserDrawer';
import { makeUserActions } from './userActions';

const CATALOG_KEY = ['t2-sec-catalog'];
const GROUP_ORDER: Area['group'][] = ['Messages', 'Contacts', 'Reports', 'Settings', 'Security'];
const GROUP_LABEL: Record<string, string> = { Security: 'Security & account' };
const HIDDEN_AREAS = ['sec_audit'];   // Audit log viewer is Phase 2 - not shown
const SAVE_REASON = 'You’re changing what groups can do and who is in them.';

interface GDraft { name: string; description: string; grants: Grants }

function LvIcon({ lv }: { lv: Level }) {
  const d: Record<string, JSX.Element> = {
    ban: <><circle cx="12" cy="12" r="10" /><path d="M4.9 4.9l14.2 14.2" /></>,
    eye: <><path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z" /><circle cx="12" cy="12" r="3" /></>,
    pen: <><path d="M12 20h9" /><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z" /></>,
    badge: <><path d="M3.85 8.62a4 4 0 0 1 4.78-4.77 4 4 0 0 1 6.74 0 4 4 0 0 1 4.78 4.78 4 4 0 0 1 0 6.74 4 4 0 0 1-4.77 4.78 4 4 0 0 1-6.75 0 4 4 0 0 1-4.78-4.77 4 4 0 0 1 0-6.76Z" /><path d="m9 12 2 2 4-4" /></>,
  };
  if (lv === 'custom') return <Ic k="puzzle" className="sc-lvi" />;
  return <svg className="sc-lvi" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{d[LV_ICON[lv]]}</svg>;
}

function areaTip(a: Area): HelpSection[] {
  const t: HelpSection[] = [];
  (['view', 'work', 'full'] as const).forEach((tier) => {
    const acts = a.actions.filter((x) => x.tier === tier);
    if (acts.length) t.push([LV[tier], acts.map((x) => x.label + (x.help ? ` - ${x.help}` : '')).join(' · ')]);
  });
  if (a.future) t.push(['Not built yet', 'The level is stored now and takes effect when this ships.']);
  return t.length ? t : [['What it covers', a.name]];
}
function areaName(a: Area): string { return a.key === 'sec_roles' ? 'User groups' : a.name; }

export default function GroupsPage({ onStateChange, onOpenUsers }: { onStateChange?: (s: SecPageState) => void; onOpenUsers: (groupId: number) => void }) {
  const qc = useQueryClient();
  const { me, hasAccess, canOpen } = useAccess();
  const secRoles = (a: string) => hasAccess('sec_roles', a) || hasAccess('sec_groups', a);
  const canSeeUsers = canOpen('sec_users');

  const catalogQ = useQuery<{ areas: Area[] }>({ queryKey: CATALOG_KEY, queryFn: () => securityApi.catalog(), staleTime: 5 * 60_000, retry: false });
  const groupsQ = useQuery<{ groups: Group[]; templates: GroupTemplate[] }>({ queryKey: GROUPS_KEY, queryFn: () => securityApi.groups(), retry: false });
  const usersQ = useQuery<UserRow[]>({ queryKey: USERS_KEY, queryFn: () => securityApi.users(), enabled: canSeeUsers, retry: false });

  const [drafts, setDrafts] = useState<Record<number, GDraft>>({});
  const [memberDraft, setMemberDraft] = useState<Record<number, number[]>>({});
  const [sel, setSel] = useState<number[]>([]);
  const [q, setQ] = useState('');
  const [diffOnly, setDiffOnly] = useState(true);
  const [areaOpen, setAreaOpen] = useState<Record<string, boolean>>({});
  const [openCk, setOpenCk] = useState<Record<string, boolean>>({});
  const [saving, setSaving] = useState(false);
  const [saveErr, setSaveErr] = useState('');
  const [addFrom, setAddFrom] = useState<number | 'new' | null>(null);
  const [renameId, setRenameId] = useState<number | null>(null);
  const [lvDlg, setLvDlg] = useState<{ gid: number; key: string } | null>(null);
  const [peopleGid, setPeopleGid] = useState<number | null>(null);
  const [person, setPerson] = useState<{ id: number; back: number } | null>(null);
  const [confirmEl, ask] = useConfirm();

  useEffect(() => {
    const on = () => { qc.invalidateQueries({ queryKey: GROUPS_KEY }); qc.invalidateQueries({ queryKey: USERS_KEY }); };
    window.addEventListener(SECURITY_CHANGED_EVENT, on);
    return () => window.removeEventListener(SECURITY_CHANGED_EVENT, on);
  }, [qc]);

  const areas = useMemo(() => (catalogQ.data?.areas ?? []).filter((a) => !HIDDEN_AREAS.includes(a.key)), [catalogQ.data]);
  const areaByKey = useMemo(() => new Map(areas.map((a) => [a.key, a])), [areas]);
  const saved = useMemo(() => groupsQ.data?.groups ?? [], [groupsQ.data]);
  const templates = groupsQ.data?.templates ?? [];
  const users = usersQ.data ?? null;
  const mine = me?.access;

  const cur = useCallback((g: Group): GDraft => drafts[g.id] ?? { name: g.name, description: g.description, grants: g.grants }, [drafts]);
  const view = useMemo(() => saved.map((g) => ({ ...g, ...cur(g) })).sort((a, b) => a.name.localeCompare(b.name)), [saved, cur]);
  const byId = useCallback((id: number) => view.find((g) => g.id === id), [view]);

  // keep a valid selection (first A-Z on open - §28)
  useEffect(() => {
    if (!view.length) return;
    setSel((s) => { const ok = s.filter((id) => view.some((g) => g.id === id)); return ok.length ? ok : [view[0].id]; });
  }, [view]);

  const savedMembers = useCallback((gid: number) => (users ?? []).filter((u) => u.groups.includes(gid)).map((u) => u.id), [users]);
  const members = useCallback((gid: number) => memberDraft[gid] ?? savedMembers(gid), [memberDraft, savedMembers]);
  const pendingGroups = useCallback((u: UserRow): number[] => {
    let gs = u.groups.slice();
    Object.entries(memberDraft).forEach(([k, ids]) => {
      const gid = Number(k);
      if (ids.includes(u.id) && !gs.includes(gid)) gs.push(gid);
      if (!ids.includes(u.id)) gs = gs.filter((x) => x !== gid);
    });
    return gs;
  }, [memberDraft]);
  const countOf = (g: Group) => (users ? members(g.id).filter((id) => { const u = users.find((x) => x.id === id); return !!u && u.status !== 'disabled'; }).length : g.memberCount);

  const groupDirty = (g: Group) => { const d = drafts[g.id]; return !!d && (d.name !== g.name || d.description !== g.description || !grantsEqual(d.grants, g.grants)); };
  const membersDirty = (gid: number) => !!memberDraft[gid] && !rowEqual(memberDraft[gid].map(String), savedMembers(gid).map(String));
  const dirty = saved.some(groupDirty) || Object.keys(memberDraft).some((k) => membersDirty(Number(k)));

  // ── draft edits ──
  function setRow(g: Group, key: string, acts: string[]) {
    const base = saved.find((x) => x.id === g.id);
    if (!base) return;
    const c = cur(base);
    const next: GDraft = { ...c, grants: { ...c.grants, [key]: acts } };
    setDrafts((d) => ({ ...d, [g.id]: next }));
  }
  function setRows(g: Group, patch: Grants) {
    const base = saved.find((x) => x.id === g.id);
    if (!base) return;
    const c = cur(base);
    setDrafts((d) => ({ ...d, [g.id]: { ...c, grants: { ...c.grants, ...patch } } }));
  }
  const isOwner = (g: Group) => g.stdKey === 'owner';
  const lockedGroup = (g: Group) => isOwner(g) || g.exceedsMe || !secRoles('edit');
  const ckKey = (gid: number, key: string) => `${gid}|${key}`;
  const shownLevel = (g: Group, a: Area): Level => (openCk[ckKey(g.id, a.key)] ? 'custom' : levelOf(a, g.grants[a.key]));

  function beyond() { toast('warning', 'That’s more than your own access', 'You can only give a group what you can do yourself.'); }

  function pickLevel(g: Group, a: Area, lv: Level, fromDlg = false) {
    if (lockedGroup(g)) return;
    const K = ckKey(g.id, a.key);
    const v = g.grants[a.key] ?? [];
    if (lv === 'custom') {
      if (shownLevel(g, a) === 'custom' && !fromDlg) { setOpenCk((o) => ({ ...o, [K]: !o[K] })); return; }
      setOpenCk((o) => ({ ...o, [K]: true }));
      if (!v.length) { const p = preset('view', a); if (addsBeyond(v, p, mine ? (mine[a.key] ?? []) : undefined)) { beyond(); return; } setRow(g, a.key, p); }
      return;
    }
    const next = preset(lv, a);
    if (addsBeyond(v, next, mine ? (mine[a.key] ?? []) : undefined)) { beyond(); return; }
    setRow(g, a.key, next);
    if (!fromDlg) setOpenCk((o) => ({ ...o, [K]: false }));
  }
  function tick(g: Group, a: Area, k: string) {
    if (lockedGroup(g)) return;
    let v = (g.grants[a.key] ?? []).slice();
    if (v.includes(k)) { v = v.filter((x) => x !== k); if (k === 'view') v = []; }
    else { v.push(k); if (k !== 'view' && !v.includes('view') && a.actions.some((x) => x.key === 'view')) v.push('view'); }
    if (addsBeyond(g.grants[a.key] ?? [], v, mine ? (mine[a.key] ?? []) : undefined)) { beyond(); return; }
    setRow(g, a.key, v);
  }
  function setMany(g: Group, rows: Area[], lv: Level) {
    const patch: Grants = {};
    let skipped = 0;
    rows.forEach((a) => {
      const next = preset(lv, a);
      if (addsBeyond(g.grants[a.key] ?? [], next, mine ? (mine[a.key] ?? []) : undefined)) { skipped++; return; }
      patch[a.key] = next;
    });
    setRows(g, patch);
    setOpenCk((o) => { const n = { ...o }; rows.forEach((a) => { delete n[ckKey(g.id, a.key)]; }); return n; });
    if (skipped) toast('warning', `${plural(skipped, 'row')} left as they were`, 'Those rows would go beyond your own access.');
  }
  function allSet(g: Group, lv: Level) {
    if (lockedGroup(g) || lv === 'custom') return;
    const live = areas.filter((a) => !a.future);   // v12.10.9.4 (#1372)
    const change = live.filter((a) => !rowEqual(g.grants[a.key], preset(lv, a)));
    if (!change.length) return;
    ask({
      sev: 'warn', title: `Set every row to ${LV[lv]}?`, label: `Set all to ${LV[lv]}`,
      body: `${plural(change.length, 'row changes', 'rows change')} for ${g.name}. Rows you’ve set individually are overwritten.`,
      onOk: () => { setMany(g, live, lv); toast('info', `${g.name}: every row set to ${LV[lv]}`, 'Press Save groups to keep it.'); },
    });
  }

  // ── save / discard ──
  const discardAll = useCallback(() => { setDrafts({}); setMemberDraft({}); setOpenCk({}); setSaveErr(''); }, []);
  async function saveAll() {
    if (!dirty || saving) return;
    setSaving(true); setSaveErr('');
    let failed = false;
    try {
      for (const g of saved) {
        if (!groupDirty(g)) continue;
        const d = drafts[g.id];
        const body: { name?: string; description?: string; grants?: Grants } = {};
        if (d.name !== g.name) body.name = d.name;
        if (d.description !== g.description) body.description = d.description;
        if (!grantsEqual(d.grants, g.grants)) body.grants = d.grants;
        await withConfirm(() => securityApi.updateGroup(g.id, body), SAVE_REASON);
        setDrafts((x) => { const n = { ...x }; delete n[g.id]; return n; });
      }
      for (const k of Object.keys(memberDraft)) {
        const gid = Number(k);
        if (!membersDirty(gid)) continue;
        await withConfirm(() => securityApi.setGroupMembers(gid, memberDraft[gid]), SAVE_REASON);
        setMemberDraft((x) => { const n = { ...x }; delete n[gid]; return n; });
      }
      setMemberDraft({});
      toast('success', 'Groups saved', 'Changes apply to every member at their next click.');
    } catch (e) {
      failed = true;
      if (!isConfirmCancelled(e)) {
        const msg = securityErrorMessage(e, 'Your changes are still here - try again, or Discard to revert.');
        setSaveErr(msg);
        toast('error', 'Could not save', msg);
      }
    } finally {
      setSaving(false);
      qc.invalidateQueries({ queryKey: GROUPS_KEY });
      qc.invalidateQueries({ queryKey: USERS_KEY });
      if (!failed) setOpenCk({});
    }
  }

  const reportRef = useRef(onStateChange);
  reportRef.current = onStateChange;
  const saveRef = useRef(saveAll); saveRef.current = saveAll;
  useEffect(() => {
    reportRef.current?.({ dirty, saving, saveAll: () => void saveRef.current(), discardAll });
  }, [dirty, saving, discardAll]);

  // ── group-level actions ──
  function resetStd(g: Group) {
    ask({
      sev: 'info', title: `Reset ${g.name} to standard?`, label: 'Reset to standard',
      body: 'What this group can do goes back to VoiceSpectrum’s standard, and that is saved now. The name stays as it is. People in this group stay in it.',
      onOk: async () => {
        await withConfirm(() => securityApi.resetGroup(g.id), `You’re resetting what ${g.name} can do.`);
        setDrafts((x) => { const n = { ...x }; delete n[g.id]; return n; });
        await qc.invalidateQueries({ queryKey: GROUPS_KEY });
        toast('success', `${g.name} reset to standard`);
      },
      errorText: (e) => (isConfirmCancelled(e) ? null : securityErrorMessage(e)),
    });
  }
  function deleteGroup(g: Group) {
    if (users) {
      const inIt = users.filter((u) => pendingGroups(u).includes(g.id));
      const only = inIt.filter((u) => pendingGroups(u).length === 1).length;
      if (only) {
        ask({ sev: 'warn', title: `${g.name} is still in use`, label: 'Show them', body: `${plural(only, 'person is', 'people are')} only in this group. Give them another group in Users first.`, onOk: () => onOpenUsers(g.id) });
        return;
      }
      const also = inIt.length;
      ask({
        sev: 'warn', title: `Delete the ${g.name} group?`, label: 'Delete group', danger: true,
        body: also ? `${plural(also, 'person is', 'people are')} also in other groups - this one is removed from them.` : 'No one is in this group.',
        onOk: () => doDelete(g), errorText: (e) => (isConfirmCancelled(e) ? null : securityErrorMessage(e)),
      });
      return;
    }
    ask({ sev: 'warn', title: `Delete the ${g.name} group?`, label: 'Delete group', danger: true, body: 'People in it keep their other groups.', onOk: () => doDelete(g), errorText: (e) => (isConfirmCancelled(e) ? null : securityErrorMessage(e)) });
  }
  async function doDelete(g: Group) {
    await withConfirm(() => securityApi.deleteGroup(g.id), `You’re deleting the ${g.name} group.`);
    setDrafts((x) => { const n = { ...x }; delete n[g.id]; return n; });
    setMemberDraft((x) => { const n = { ...x }; delete n[g.id]; return n; });
    setSel((s) => s.filter((id) => id !== g.id));
    await qc.invalidateQueries({ queryKey: GROUPS_KEY });
    qc.invalidateQueries({ queryKey: USERS_KEY });
    toast('success', `${g.name} deleted`);
  }

  const actions = useMemo(() => makeUserActions({
    users: users ?? [], groups: saved, policy: me?.policy ?? null, ask,
    onChanged: () => { qc.invalidateQueries({ queryKey: USERS_KEY }); qc.invalidateQueries({ queryKey: GROUPS_KEY }); },
  }), [users, saved, me, ask, qc]);

  // ── render ──
  if (catalogQ.isLoading || groupsQ.isLoading) return <div className="vs-sec sec-page"><div className="sec-loading">Loading user groups…</div></div>;
  if (groupsQ.isError) return <div className="settings-error">{securityErrorMessage(groupsQ.error, 'User groups could not be loaded.')}</div>;
  if (catalogQ.isError) return <div className="settings-error">{securityErrorMessage(catalogQ.error, 'The list of areas could not be loaded.')}</div>;
  if (!view.length) return <div className="vs-sec sec-page"><div className="sc-empty"><b>No groups yet</b><span>Groups appear here once your organization is set up.</span></div></div>;

  const selected = sel.map(byId).filter((g): g is Group => !!g);
  const single = selected.length === 1 ? selected[0] : null;
  const qq = q.trim().toLowerCase();
  const match = (a: Area) => !qq || `${areaName(a)} ${a.section ?? ''} ${a.group} ${GROUP_LABEL[a.group] ?? ''}`.toLowerCase().includes(qq);

  function pickGroup(id: number) {
    setSel((s) => (s.includes(id) ? (s.length > 1 ? s.filter((x) => x !== id) : s) : [...s, id]));
  }

  const pills = (
    <div className="sc-rpills">
      {view.map((g) => {
        const on = sel.includes(g.id);
        const base = saved.find((s) => s.id === g.id)!;
        const d = groupDirty(base) || membersDirty(g.id);
        return (
          <span key={g.id} className={`sc-rpill${on ? ' on' : ''}${d ? ' dirty' : ''}`} title={d ? 'Changed - not saved yet' : undefined}>
            <button type="button" className="sc-rpill-b" aria-pressed={on} onClick={() => pickGroup(g.id)}>
              {on && <Ic k="check" />}{g.name}<span className="n">{countOf(g)}</span>
            </button>
            <HelpTooltip sections={[['What it’s for', g.description || '—'], ['Type', g.builtin ? (isOwner(g) ? 'Built in · always full access' : 'Built in') : 'Custom']]} />
          </span>
        );
      })}
      <button type="button" className="sc-rpill add" disabled={!secRoles('create')} title={secRoles('create') ? undefined : 'Your group can’t create groups'} onClick={() => setAddFrom('new')}><Ic k="plus" /> Add group</button>
    </div>
  );

  const bar = single ? (() => {
    const g = single;
    const n = countOf(g);
    const std = g.stdKey ? STD_NAMES[g.stdKey] : undefined;
    const canPeople = canSeeUsers && (secRoles('members') || secRoles('people') || secRoles('view'));
    return (
      <div className="sc-rbar">
        <div className="sc-rbar-t">
          <b>{g.name}</b>
          {g.builtin ? <span className="sc-rtag b"><Ic k="lock" />Built in</span> : <span className="sc-rtag">Custom</span>}
          <button type="button" className="sc-link" disabled={!canPeople} onClick={() => setPeopleGid(g.id)}>{n ? `${plural(n, 'person is', 'people are')} in this group` : 'No one is in this group - add people'}</button>
          {g.builtin && std && g.name !== std && <span className="sc-idst" style={{ display: 'inline' }}>· standard name: {std}</span>}
        </div>
        <div className="sc-rd-acts">
          {secRoles('rename') && <button type="button" className="sc-secondary" onClick={() => setRenameId(g.id)}><Ic k="pen" /> Rename</button>}
          {secRoles('create') && <button type="button" className="sc-secondary" onClick={() => setAddFrom(g.id)}><Ic k="copy" /> Duplicate</button>}
          {g.builtin && !isOwner(g) && secRoles('edit') && !g.exceedsMe && <button type="button" className="sc-secondary" onClick={() => resetStd(g)}><Ic k="reset" /> Reset to standard</button>}
          {!g.builtin && secRoles('del') && <button type="button" className="sc-danger" onClick={() => deleteGroup(g)}><Ic k="trash" /> Delete</button>}
        </div>
      </div>
    );
  })() : (
    <div className="sc-rbar">
      <div className="sc-rbar-t"><b>Comparing {selected.length} groups</b><span className="sc-idst" style={{ display: 'inline' }}>Click any level to change it, or a column name to edit just that group.</span></div>
      <div className="sc-rd-acts"><label className="sc-difft"><Toggle on={diffOnly} onChange={setDiffOnly} label="Show only differences" />Show only differences</label></div>
    </div>
  );

  // ── single-group detail ──
  // v12.10.9.4 (#1378, UI-PROTO-T40 LOCKED 10/7): every row shows the SAME five columns - None · View · Work · Full · Custom -
  // with the icon before the word; an option the row doesn't have stays in its place, faded and not clickable.
  // (#1372) A FUTURE row is fully disabled: nothing selected, nothing selectable, no Custom menu.
  const offers = (a: Area, o: Level) => lvList(a).includes(o) && (o !== 'custom' || (a.actions ?? []).length > 1);
  function levelSeg(g: Group, a: Area, fromDlg = false) {
    const lv = shownLevel(g, a);
    const open = !!openCk[ckKey(g.id, a.key)];
    const locked = lockedGroup(g);
    return (
      <div className="seg-group sc-rseg sc-rseg5" role="radiogroup" aria-label={`${g.name}: ${areaName(a)}`}>
        {(['none', 'view', 'work', 'full', 'custom'] as Level[]).map((o) => {
          const avail = !a.future && offers(a, o);
          const on = avail && lv === o;
          return (
            <button key={o} type="button" className={`seg sc-lvseg lv-${o}${o === 'custom' ? ' sc-cus' : ''}${on ? ' on' : ''}${avail ? '' : ' na'}`} role="radio" aria-checked={on}
              title={a.future ? 'Not available yet' : avail ? `${LV[o]}: ${levelHelp(a, o)}` : 'Not available for this area'} disabled={locked || !avail} onClick={() => pickLevel(g, a, o, fromDlg)}>
              <LvIcon lv={o} />{LV[o]}{o === 'custom' && avail && !fromDlg && <span className="sc-car">{on && open ? '▴' : '▾'}</span>}
            </button>
          );
        })}
      </div>
    );
  }
  // v12.10.9.4 (#1377): one line per area (MESSAGES, CONTACTS, REPORTS, SETTINGS, SECURITY, and each Settings area) sets every
  // row under it at once; a row that can't go that high gets its highest level (preset clamps). Custom on the line is never
  // clicked - it lights up as Mixed when the rows differ. FUTURE rows are left out.
  function areaLine(g: Group, rows: Area[], label: string) {
    const live = rows.filter((a) => !a.future);
    const vals = live.map((a) => shownLevel(g, a));
    const same = vals.length > 0 && vals.every((v) => v === vals[0]);
    const locked = lockedGroup(g) || !live.length;
    return (
      <div className="seg-group sc-rseg sc-rseg5" role="radiogroup" aria-label={`${label}: every row`}>
        {(['none', 'view', 'work', 'full', 'custom'] as Level[]).map((o) => {
          if (o === 'custom') {
            const mixed = live.length > 0 && !same;
            return <button key={o} type="button" className={`seg sc-lvseg lv-custom sc-cus${mixed ? ' sc-mixline' : ' na'}`} disabled title={mixed ? 'Rows are set differently - change single rows below' : 'Pick a level, or change single rows below'}><LvIcon lv={o} />{mixed ? 'Mixed' : LV[o]}</button>;
          }
          const avail = live.some((a) => offers(a, o));
          const on = avail && same && vals[0] === o;
          return (
            <button key={o} type="button" className={`seg sc-lvseg lv-${o}${on ? ' on' : ''}${avail ? '' : ' na'}`} role="radio" aria-checked={on} disabled={locked || !avail}
              title={avail ? `${LV[o]} for every row in ${label}` : 'Not available for this area'} onClick={() => setMany(g, live, o)}><LvIcon lv={o} />{LV[o]}</button>
          );
        })}
      </div>
    );
  }
  function checklist(g: Group, a: Area) {
    const v = g.grants[a.key] ?? [];
    const locked = lockedGroup(g);
    const box = (x: Area['actions'][number]) => {
      const on = v.includes(x.key);
      const risk = x.key === 'del' || (x.tier === 'full' && x.key !== 'all' && x.key !== 'download' && x.key !== 'path');   // v12.10.14 (#1618)
      return (
        <button key={x.key} type="button" className={`sc-xc${risk ? ' risk' : ''}${on ? ' on' : ''}`} title={x.help || x.label} disabled={locked} aria-pressed={on} onClick={() => tick(g, a, x.key)}>
          <span className="bx">{on ? '✓' : ''}</span>{x.label}
        </button>
      );
    };
    // v12.10.9.4 (#1381, UI-PROTO-T40 LOCKED 10/7): one tick per action, in named groups under each level -
    // VIEW: See · Copy · Export; WORK: Handle · Communicate; FULL (v12.10.10: none for Messages). Other areas keep one plain row of ticks.
    const SUB: Record<string, string> = { path: 'See', transcript: 'See', data: 'See', copytr: 'Copy', copydata: 'Copy', copy: 'Copy',
      // v12.10.10 (#1400): the row is 'Export' (was 'Print / Report') and carries Download recording too.
      export: 'Export', print: 'Export', download: 'Export',
      flag: 'Handle', complete: 'Handle', note: 'Handle', reminder: 'Handle', label: 'Handle', assign: 'Handle',   // v12.10.10 (#1399)
      text: 'Communicate', call: 'Communicate', play: 'Communicate' };
    const isMsg = a.key === 'phone' || a.key === 'texts';
    // v12.10.10 (#1400): Download recording is SHOWN in the View level's Export row but stays a Full-tier action - only
    // Full ticks it by default; ticking it by hand makes the area Custom (#1381 rule). So Messages has no Full group.
    // v12.10.14 (#1618): Call path too - a Full-tier tick (Owner only by default) shown in the See row.
    const shownTier = (x: Area['actions'][number]) => (isMsg && (x.key === 'download' || x.key === 'path') ? 'view' : x.tier);
    const grp = (tier: 'view' | 'work' | 'full') => {
      // v12.10.26 (#2138 T192): Call path is folded into the hidden VoiceSpectrum Support level - no practice group can give it, so the tick is gone.
      const its = a.actions.filter((x) => shownTier(x) === tier && !(isMsg && x.key === 'path'));
      if (!its.length) return null;
      if (!isMsg) {
        return (
          <div className="sc-cg" key={tier}>
            <div className="gl"><LvIcon lv={tier} />{LV[tier]}</div>
            <div className="items">{its.map(box)}</div>
          </div>
        );
      }
      const order: string[] = [];
      its.forEach((x) => { const g = x.key === 'view' ? 'See' : (SUB[x.key] ?? ''); if (!order.includes(g)) order.push(g); });
      return (
        <div className="sc-cg" key={tier}>
          <div className="gl"><LvIcon lv={tier} />{LV[tier]}</div>
          <div className="items sc-subgroups">
            {order.map((g) => (
              <span key={g || 'x'} className="sc-com"><span className="lab">{g}</span>{its.filter((x) => (x.key === 'view' ? 'See' : (SUB[x.key] ?? '')) === g).map(box)}</span>
            ))}
          </div>
        </div>
      );
    };
    return <div className="sc-ckin">{grp('view')}{grp('work')}{grp('full')}</div>;
  }
  function row(g: Group, a: Area, cls = '') {
    const base = saved.find((s) => s.id === g.id);
    const edited = !!base && !rowEqual(base.grants[a.key], g.grants[a.key]);
    const lv = shownLevel(g, a);
    const open = lv === 'custom' && !a.future && !!openCk[ckKey(g.id, a.key)];
    return (
      <div key={a.key}>
        <div className={`sc-rrow${cls ? ` ${cls}` : ''}${edited ? ' sc-edited' : ''}${a.future ? ' sc-futrow' : ''}`} title={edited ? 'Changed - not saved yet' : undefined}>
          <span className="sc-rrow-n"><span className="sc-rrow-lab">{areaName(a)}</span><HelpTooltip sections={areaTip(a)} />{a.future && <span className="sc-future">Future</span>}</span>
          <span className="sc-rrow-r">
            {lv === 'custom' && !open && !a.future && <span className="sc-chint">{customHint(a, g.grants[a.key] ?? [])}</span>}
            {levelSeg(g, a)}
          </span>
        </div>
        {open && <div className="sc-ck">{checklist(g, a)}</div>}
      </div>
    );
  }
  function detail(g: Group) {
    const locked = lockedGroup(g);
    const base = saved.find((s) => s.id === g.id)!;
    const out: JSX.Element[] = [];
    if (isOwner(g)) out.push(<div key="own" className="sc-lock-note" style={{ margin: '0 0 6px' }}><Ic k="lock" /><span>The Owner always has full access, so no one can take it away by accident. You can rename it.</span></div>);
    else if (g.exceedsMe) out.push(<div key="ex" className="sc-lock-note" style={{ margin: '0 0 6px' }}><Ic k="lock" /><span>This group gives more access than you have, so you can’t change what it allows or who is in it.</span></div>);
    else if (!secRoles('edit')) out.push(<div key="ro" className="sc-lock-note" style={{ margin: '0 0 6px' }}><Ic k="lock" /><span>You can see what this group allows but not change it.</span></div>);
    if (!qq) {
      const lvls = areas.filter((a) => !a.future).map((a) => shownLevel(g, a));   // v12.10.9.4 (#1372): FUTURE rows don't count
      const uni: Level = lvls.length && lvls.every((v) => v === lvls[0]) ? lvls[0] : 'custom';
      out.push(
        <div key="all" className="sc-rsec"><h4>Everything <span>starting point for every row below</span></h4><div className="sc-rrows">
          <div className="sc-rrow sc-allrow">
            <span className="sc-rrow-n">All rows<HelpTooltip sections={[['None', 'Can’t open it - it isn’t in their menu.'], ['View', 'Read only.'], ['Work', 'View + everyday actions. Records: edit, communicate (text · call back), listen. Reports: export and print. Settings: change and save.'], ['Full', 'Work + delete. Reports: every report in the category, including restricted ones.'], ['Custom', 'Your own mix - click Custom on any row to tick exactly what’s allowed. Picking a level on this row sets every row below.']]} />{uni === 'custom' && <span className="sc-mixed">Mixed</span>}</span>
            <div className="seg-group sc-rseg sc-rseg5" role="radiogroup" aria-label="All rows">
              {(['none', 'view', 'work', 'full', 'custom'] as Level[]).map((o) => {
                const on = uni === o;
                return (
                  <button key={o} type="button" className={`seg sc-lvseg lv-${o}${o === 'custom' ? ' sc-cus' : ''}${on ? ' on' : ''}`} role="radio" aria-checked={on}
                    disabled={locked || o === 'custom'} title={o === 'custom' ? 'Shown when rows are set differently - change individual rows below' : `${LV[o]} on every row`} onClick={() => allSet(g, o)}>
                    <LvIcon lv={o} />{LV[o]}
                  </button>
                );
              })}
            </div>
          </div>
        </div></div>,
      );
    }
    if (!qq || 'whose messages scope'.includes(qq)) {
      out.push(
        <div key="scope" className="sc-rsec"><h4>Scope</h4><div className="sc-rrows">
          <div className="sc-rrow"><span className="sc-rrow-n">Whose messages<HelpTooltip sections={[['What it covers', 'Whether people in this group see every message and call, or only those assigned to them. Fixed to Everyone until this ships.'], ['Example', <><em>Assigned to me</em> → a provider sees only the messages routed to them.</>]]} /><span className="sc-future">Future</span></span>
            <span className="sc-rrow-r"><span className="sc-fixed">Everyone</span></span></div>
        </div></div>,
      );
    }
    let any = false;
    GROUP_ORDER.forEach((G) => {
      const rows = areas.filter((a) => a.group === G);
      if (!rows.length) return;
      const liveRows = rows.filter((a) => !a.future);   // v12.10.9.4 (#1372): the counter ignores FUTURE rows
      const allowed = liveRows.filter((a) => (g.grants[a.key] ?? []).length > 0).length;
      const body: JSX.Element[] = [];
      if (G === 'Settings') {
        const sections = [...new Set(rows.map((a) => a.section ?? 'Settings'))];
        sections.forEach((sec) => {
          const secs = rows.filter((a) => (a.section ?? 'Settings') === sec);
          const areaHit = !qq || sec.toLowerCase().includes(qq) || 'settings'.includes(qq);
          const hits = qq ? secs.filter(match) : secs;
          if (!areaHit && !hits.length) return;
          const vals = secs.map((a) => shownLevel(g, a));
          const same = vals.every((v) => v === vals[0]);
          const open = !!areaOpen[sec] || (!!qq && !areaHit && hits.length > 0);
          const anyEdit = secs.some((a) => !rowEqual(base.grants[a.key], g.grants[a.key]));
          body.push(
            <div key={`area-${sec}`} className={`sc-rrow area${open ? ' open' : ''}${anyEdit ? ' sc-edited' : ''}`}>
              <button type="button" className="sc-rrow-n sc-area-t" aria-expanded={open} onClick={() => setAreaOpen((o) => ({ ...o, [sec]: !o[sec] }))}>
                <Ic k="chev" />{sec}<span className="sc-secn">{plural(secs.length, 'section')}</span>
                {!same && <span className="sc-mixed">Mixed · {(['full', 'work', 'custom', 'view', 'none'] as Level[]).map((v) => { const c = vals.filter((x) => x === v).length; return c ? `${c} ${LV[v]}` : ''; }).filter(Boolean).join(', ')}</span>}
              </button>
              {areaLine(g, secs, sec)}
            </div>,
          );
          if (open) (areaHit ? secs : hits).forEach((a) => body.push(row(g, a, 'sub')));
        });
      } else {
        rows.filter(match).forEach((a) => body.push(row(g, a)));
      }
      if (body.length) {
        any = true;
        // v12.10.9.4 (#1377): the area's own line - its name, the count, and the five-column picker for every row in it.
        out.push(<div key={G} className="sc-rsec"><div className={`sc-rrow sc-grpline${liveRows.length ? '' : ' sc-futrow'}`}>
          <span className="sc-rrow-n"><b>{(GROUP_LABEL[G] ?? G).toUpperCase()}</b>{liveRows.length ? <span className="sc-grpn">{allowed} of {liveRows.length} allowed</span> : <span className="sc-future">Future</span>}</span>
          <span className="sc-rrow-r">{areaLine(g, rows, GROUP_LABEL[G] ?? G)}</span>
        </div><div className="sc-rrows">{body}</div></div>);
      }
    });
    if (!any && qq) out.push(<div key="none" className="sc-empty" style={{ marginTop: 12 }}><b>Nothing matches “{q.trim()}”</b><span>Try another word, like “reports”, “texts” or “Personalization”.</span></div>);
    return out;
  }

  // ── compare ──
  function compare() {
    const cols = `minmax(190px,1.5fr) repeat(${selected.length},minmax(110px,1fr))`;
    let n = 0;
    const rows = areas.filter(match).map((a) => {
      const vals = selected.map((g) => JSON.stringify((g.grants[a.key] ?? []).slice().sort()));
      if (diffOnly && new Set(vals).size < 2) return null;
      n++;
      return (
        <div key={a.key} className="sc-cmp-r" style={{ gridTemplateColumns: cols }}>
          <span>{areaName(a)}{a.section && <span className="sc-atag">{a.section}</span>}</span>
          {selected.map((g) => {
            const lv = levelOf(a, g.grants[a.key]);
            const base = saved.find((s) => s.id === g.id);
            const ed = !!base && !rowEqual(base.grants[a.key], g.grants[a.key]);
            const pill = <span className={`sc-lvl ${lv}`}><LvIcon lv={lv} />{LV[lv]}</span>;
            if (lockedGroup(g)) return <span key={g.id} title={isOwner(g) ? 'Owner always has full access' : levelHelp(a, lv)}>{pill}</span>;
            return (
              <span key={g.id}>
                <button type="button" className={`sc-cell${ed ? ' edited' : ''}`} title={lv === 'custom' ? customHint(a, g.grants[a.key] ?? []) : levelHelp(a, lv)}
                  aria-label={`${g.name}: ${areaName(a)} - ${LV[lv]}`} onClick={() => setLvDlg({ gid: g.id, key: a.key })}>{pill}<Ic k="chevDown" /></button>
              </span>
            );
          })}
        </div>
      );
    });
    return (
      <div className="sc-cmp">
        <div className="sc-cmp-h" style={{ gridTemplateColumns: cols }}>
          <span>{diffOnly ? 'Rows where they differ' : 'All rows'}</span>
          {selected.map((g) => <span key={g.id}><button type="button" className="sc-link" title={`Edit only ${g.name}`} onClick={() => setSel([g.id])}>{g.name}</button></span>)}
        </div>
        {rows}
        {!n && <div className="sc-cmp-r" style={{ gridTemplateColumns: '1fr' }}><span className="sc-idst">{qq ? `Nothing matches “${q.trim()}”.` : 'These groups can do exactly the same things.'}</span></div>}
      </div>
    );
  }

  const dlgGroup = lvDlg ? byId(lvDlg.gid) : undefined;
  const dlgArea = lvDlg ? areaByKey.get(lvDlg.key) : undefined;
  const peopleGroup = peopleGid !== null ? byId(peopleGid) : undefined;
  const personRow = person && users ? users.find((u) => u.id === person.id) : undefined;
  const personBack = person ? byId(person.back) : undefined;

  return (
    <div className="vs-sec sec-page">
      {saveErr && <div style={{ maxWidth: 1100, marginBottom: 12 }}><InlineError text={saveErr} /></div>}
      {pills}
      {bar}
      <div className="sc-rl-detail">
        <div className="sc-rsearch"><label className="sc-search"><Ic k="search" /><input placeholder="Search rows - e.g. vocabulary, contacts, reports" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Search rows" /></label></div>
        {single ? detail(single) : compare()}
      </div>

      {lvDlg && dlgGroup && dlgArea && (
        <ScModal className="lvdlg" label="Change level" onClose={() => { setOpenCk((o) => { const n = { ...o }; delete n[ckKey(dlgGroup.id, dlgArea.key)]; return n; }); setLvDlg(null); }}>
          <h4>{dlgGroup.name} · {areaName(dlgArea)}</h4>
          <p>{levelOf(dlgArea, dlgGroup.grants[dlgArea.key]) === 'custom' ? `Custom - ${customHint(dlgArea, dlgGroup.grants[dlgArea.key] ?? [])}` : levelHelp(dlgArea, levelOf(dlgArea, dlgGroup.grants[dlgArea.key]))}</p>
          <div className="sc-pan"><h6>Level</h6>{levelSeg(dlgGroup, dlgArea, true)}</div>
          <div className="sc-pan"><h6>What this allows</h6><div className="sc-ck">{checklist(dlgGroup, dlgArea)}</div></div>
          <div className="mact"><button type="button" className="ma" onClick={() => { setOpenCk((o) => { const n = { ...o }; delete n[ckKey(dlgGroup.id, dlgArea.key)]; return n; }); setLvDlg(null); }}>Done</button></div>
        </ScModal>
      )}

      {addFrom !== null && (
        <AddGroupModal groups={view} templates={templates} from={addFrom === 'new' ? (sel[0] ?? null) : addFrom} duplicate={addFrom !== 'new'}
          onClose={() => setAddFrom(null)}
          onCreated={async (g) => { await qc.invalidateQueries({ queryKey: GROUPS_KEY }); setSel([g.id]); toast('success', 'Group added', 'Change what differs, then Save groups.'); }} />
      )}
      {renameId !== null && byId(renameId) && (
        <RenameModal group={byId(renameId)!} groups={view} onClose={() => setRenameId(null)}
          onApply={(name) => { const base = saved.find((s) => s.id === renameId)!; const c = cur(base); setDrafts((d) => ({ ...d, [base.id]: { ...c, name } })); setRenameId(null); }} />
      )}
      {peopleGroup && users && (
        <PeopleDrawer group={peopleGroup} users={users} groups={view} members={members(peopleGroup.id)} pendingGroups={pendingGroups}
          savedMembers={savedMembers(peopleGroup.id)} canChange={(secRoles('members') || secRoles('people')) && !peopleGroup.exceedsMe}
          onChange={(ids) => setMemberDraft((d) => ({ ...d, [peopleGroup.id]: ids }))}
          onClose={() => setPeopleGid(null)}
          onOpenUsers={() => { setPeopleGid(null); onOpenUsers(peopleGroup.id); }}
          onOpenPerson={(u) => { if (u.self) { openYourAccount(); return; } setPerson({ id: u.id, back: peopleGroup.id }); setPeopleGid(null); }} />
      )}
      {personRow && users && (
        <UserDrawer key={personRow.id} user={personRow} users={users} groups={saved} policy={me?.policy ?? null} actions={actions}
          onClose={() => setPerson(null)} onSaved={() => { qc.invalidateQueries({ queryKey: USERS_KEY }); qc.invalidateQueries({ queryKey: GROUPS_KEY }); }}
          backTo={personBack ? { label: `${personBack.name} (${countOf(personBack)})`, onBack: () => { const b = person!.back; setPerson(null); setPeopleGid(b); } } : undefined} />
      )}
      {confirmEl}
    </div>
  );
}

// ── Add / Duplicate group ───────────────────────────────────────────────────────
function AddGroupModal({ groups, templates, from, duplicate, onClose, onCreated }: {
  groups: Group[]; templates: GroupTemplate[]; from: number | null; duplicate: boolean; onClose: () => void; onCreated: (g: Group) => void | Promise<void>;
}) {
  const [name, setName] = useState('');
  const [src, setSrc] = useState<string>(from !== null ? `g:${from}` : (groups[0] ? `g:${groups[0].id}` : '__empty'));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => { ref.current?.focus({ preventScroll: true }); }, []);
  const n = name.trim();
  const taken = !!n && groups.some((g) => g.name.toLowerCase() === n.toLowerCase());
  const ok = !!n && !taken;
  async function go() {
    if (!ok || busy) return;
    setBusy(true); setErr('');
    try {
      const body: { name: string; fromGroupId?: number; templateKey?: GroupTemplate['key']; description?: string } = { name: n };
      if (src.startsWith('g:')) { const id = Number(src.slice(2)); body.fromGroupId = id; const g = groups.find((x) => x.id === id); if (g) body.description = `Custom group based on ${g.name}.`; }
      else if (src.startsWith('t:')) { const t = templates.find((x) => x.key === src.slice(2)); if (t) { body.templateKey = t.key; body.description = `Custom group based on ${t.name} (template).`; } }
      else body.description = 'Custom group. Add only what it should allow - pair it with another group.';
      const g = await withConfirm(() => securityApi.createGroup(body), 'You’re adding a user group.');
      await onCreated(g);
      onClose();
    } catch (e) {
      if (!isConfirmCancelled(e)) setErr(securityErrorMessage(e, 'The group wasn’t added. Try again.'));
    } finally { setBusy(false); }
  }
  return (
    <ScModal onClose={onClose} label={duplicate ? 'Duplicate group' : 'Add a group'} closeOnScrim={false}>
      <h4>{duplicate ? 'Duplicate group' : 'Add a group'}</h4>
      <p>Start from the closest group, then change only the rows that differ.</p>
      <div className="mrow"><label htmlFor="grpName">Name</label>
        <input id="grpName" ref={ref} maxLength={32} placeholder="e.g. Team reports" value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') void go(); }} />
        {taken && <span className="sc-err">A group with this name already exists.</span>}</div>
      <div className="mrow"><label htmlFor="grpFrom">Start from</label>
        <select id="grpFrom" value={src} onChange={(e) => setSrc(e.target.value)}>
          <optgroup label="Your groups">{sortGroups(groups).map((g) => <option key={g.id} value={`g:${g.id}`} disabled={g.exceedsMe}>{g.name}{g.exceedsMe ? ' — exceeds your access' : ''}</option>)}</optgroup>
          {templates.length > 0 && <optgroup label="Templates">{templates.map((t) => <option key={t.key} value={`t:${t.key}`}>{t.name} — {t.description}</option>)}</optgroup>}
          <option value="__empty">Nothing - start with no access</option>
        </select></div>
      {err && <div style={{ marginBottom: 12 }}><InlineError text={err} /></div>}
      <div className="mact">
        <button type="button" className="mc" onClick={onClose}>Cancel</button>
        <button type="button" className="ma" disabled={!ok || busy} onClick={() => void go()}><Ic k="plus" /> {busy ? 'Adding…' : 'Add group'}</button>
      </div>
    </ScModal>
  );
}

// ── Rename (draft - saved with Save groups) ─────────────────────────────────────
function RenameModal({ group, groups, onClose, onApply }: { group: Group; groups: Group[]; onClose: () => void; onApply: (name: string) => void }) {
  const [name, setName] = useState(group.name);
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => { ref.current?.focus({ preventScroll: true }); ref.current?.select(); }, []);
  const std = group.stdKey ? STD_NAMES[group.stdKey] : undefined;
  const n = name.trim();
  const ok = !!n && !groups.some((g) => g.id !== group.id && g.name.toLowerCase() === n.toLowerCase());
  return (
    <ScModal onClose={onClose} label="Rename group" closeOnScrim={false}>
      <h4>Rename group</h4>
      <p>{group.builtin ? <>Use the word your organization uses. What the group can do doesn’t change.{std && <> Standard name: <b>{std}</b>.</>}</> : 'Only the name changes.'}</p>
      <div className="mrow"><label htmlFor="grpRename">Name</label>
        <input id="grpRename" ref={ref} maxLength={32} value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter' && ok) onApply(n); }} />
        {!ok && n && <span className="sc-err">A group with this name already exists.</span>}</div>
      <div className="mact">
        {group.builtin && std && group.name !== std && <button type="button" className="sc-link sc-alt" onClick={() => onApply(std)}>Reset to “{std}”</button>}
        <button type="button" className="mc" onClick={onClose}>Cancel</button>
        <button type="button" className="ma" disabled={!ok} onClick={() => onApply(n)}>Rename</button>
      </div>
    </ScModal>
  );
}

// ── People in this group (drawer; changes saved with Save groups) ───────────────────
function PeopleDrawer({ group, users, groups, members, savedMembers, pendingGroups, canChange, onChange, onClose, onOpenUsers, onOpenPerson }: {
  group: Group; users: UserRow[]; groups: Group[]; members: number[]; savedMembers: number[]; pendingGroups: (u: UserRow) => number[];
  canChange: boolean; onChange: (ids: number[]) => void; onClose: () => void; onOpenUsers: () => void; onOpenPerson: (u: UserRow) => void;
}) {
  useEscClose(onClose);
  const inNow = users.filter((u) => members.includes(u.id));
  const removed = users.filter((u) => savedMembers.includes(u.id) && !members.includes(u.id));
  const shown = [...inNow, ...removed].sort((a, b) => displayName(a).localeCompare(displayName(b)));
  const avail = users.filter((u) => u.status !== 'disabled' && !members.includes(u.id)).sort((a, b) => displayName(a).localeCompare(displayName(b)));
  const managers = (list: UserRow[]) => list.filter((x) => x.status === 'active' && pendingGroups(x).some((id) => groupManagesUsers(groups.find((g) => g.id === id))));
  const nameOf = (id: number) => groups.find((g) => g.id === id)?.name ?? '—';
  return createPortal(
    <div className="vs-sec sc-drawer-scrim" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="sc-drawer" role="dialog" aria-modal="true" aria-label={`People in ${group.name}`}>
        <div className="dw-head">
          <div className="sc-dw-id"><div style={{ minWidth: 0 }}><h4>People in this group</h4><p>{group.name} · {plural(inNow.length, 'person', 'people')}</p></div></div>
          <button type="button" className="modal-x" onClick={onClose} aria-label="Close"><Ic k="x" /></button>
        </div>
        <div className="dw-body">
          <div className="sc-dw-sec">
            <h5>Add people</h5>
            <p className="sc-sub">They keep their other groups; {group.name} is added on top. Saved with <em>Save groups</em>.</p>
            {group.exceedsMe ? (
              <div className="sc-lock-note"><Ic k="lock" /><span>This group gives more access than you have, so you can’t add people to it.</span></div>
            ) : !canChange ? (
              <div className="sc-lock-note"><Ic k="lock" /><span>Your group can’t add or remove people here.</span></div>
            ) : avail.length ? (
              <select className="sc-role-select" aria-label="Add a person" value="" onChange={(e) => { const id = Number(e.target.value); if (id) onChange([...members, id]); }}>
                <option value="">+ Add people…</option>
                {avail.map((u) => <option key={u.id} value={u.id}>{displayName(u)}{u.groups.length ? ` — ${nameOf(pendingGroups(u)[0])}${pendingGroups(u).length > 1 ? ` +${pendingGroups(u).length - 1}` : ''}` : ''}</option>)}
              </select>
            ) : <p className="sc-sub" style={{ margin: 0 }}>Everyone is already in this group.</p>}
          </div>
          <div className="sc-dw-sec">
            <h5>{plural(inNow.length, 'person', 'people')}</h5>
            {!shown.length && <p className="sc-sub" style={{ margin: 0 }}>No one is in this group yet.</p>}
            {shown.map((u) => {
              const pg = pendingGroups(u);
              const has = members.includes(u.id);
              const added = has && !savedMembers.includes(u.id);
              const isRemoved = !has && savedMembers.includes(u.id);
              const others = pg.filter((id) => id !== group.id).map(nameOf);
              const only = has && pg.length === 1;
              const mgrs = managers(users);
              const lastMgr = has && groupManagesUsers(group) && u.status === 'active' && mgrs.length === 1 && mgrs[0].id === u.id
                && !pg.filter((id) => id !== group.id).some((id) => groupManagesUsers(groups.find((g) => g.id === id)));
              const why = only ? 'This is their only group - give them another group first.' : lastMgr ? 'They’re the only person who can manage users and groups.' : '';
              return (
                <div key={u.id} className={`sc-pp${isRemoved ? ' removed' : ''}`}>
                  <Avatar u={u} />
                  <button type="button" className="sc-pp-main" onClick={() => onOpenPerson(u)}>
                    <b>{displayName(u)}{u.self && <span className="sc-you">You</span>}</b>
                    <span>{identity(u)}{isInvite(u) ? ' · invited' : ''}</span>
                    {others.length > 0 && <span className="sc-pp-also">also {others.join(', ')}</span>}
                  </button>
                  <div className="sc-pp-r">
                    {added && <span className="sc-chg">Added - not saved</span>}
                    {isRemoved && <span className="sc-chg">Removed - not saved</span>}
                    {canChange && !group.exceedsMe && (added || isRemoved
                      ? <button type="button" className="sc-link" onClick={() => onChange(added ? members.filter((x) => x !== u.id) : [...members, u.id])}>Undo</button>
                      : <button type="button" className="sc-link" disabled={!!why || u.self} title={u.self ? 'Nobody changes their own groups' : why || undefined} onClick={() => onChange(members.filter((x) => x !== u.id))}>Remove from this group</button>)}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        <div className="dw-foot">
          <button type="button" className="sc-link sc-foot-l" onClick={onOpenUsers}>Open in Users</button>
          <button type="button" className="ma" onClick={onClose}>Done</button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
